import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { NumericFormat } from 'react-number-format';

// ✅ Picker (Hook + Stable Modal)
import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';

// ✅ API (لجلب تفاصيل العرض + جلب جميع وحدات الصنف)
import { fetchItemsWithUomLookup, type ItemWithUomRow } from '@pages/INV/lookups/itemsWithUomLookup.api';
import { useDocumentCalculator } from '@Helper/documents/DocCalc/useDocumentCalculator';
import { DocTotalsPanel } from '@Helper/documents/DocCalc/DocTotalsPanel';
import { num } from '@Helper/documents/DocCalc/round';
import { Card } from '@components/card/card';
import { Form } from 'react-bootstrap';
import NeatixModal from '@components/Modal/NeatixModal';
// import Decimal from 'decimal.js';

interface LinesProps {
  formik: FormikProps<any>;
  maxTableHeight?: string;
  isUpdate?: boolean; // اختياري (لو شاشة أمر الشراء لها وضع تعديل)
}

type ItemType = 'InventoryItem' | 'ExpenseItem' | 'FixedAssetItem' | 'ServiceItem' | 'UndefinedItem';

const EACH_UOM_ID = 'c0391001-ffc5-4a91-9470-cb9e8ac2a650'; // Each  في حالة تحويل من طلب شراء احتاجها

const DEFAULT_ITEM_TYPE: ItemType = 'InventoryItem';

const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const round2 = (v: any) => {
  const n = Number(v || 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(n, max));

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));

const POLines: React.FC<LinesProps> = ({ formik, maxTableHeight = '500px', isUpdate = false }) => {
  const { values } = formik;
  const { i18n, t } = useTranslation();

  // console.log(values.transactionsLinesList);

  const inventoryId: string | null = values?.inventoryId ?? null;

  // ✅ Picker
  const { pickItemUom, modalProps } = useItemUomPicker();

  // ✅ Map لعرض أسماء الأصناف والوحدات + باركود (ويدعم التعديل)
  const [detailsMap, setDetailsMap] = useState<Map<string, ItemWithUomRow>>(new Map());
  const [detailsLoading, setDetailsLoading] = useState(false);

  // ✅ Abort لجلب التفاصيل (update-mode + uoms preload)
  const abortRef = useRef<AbortController | null>(null);
  // حالة اضافة صنف جديد
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  // =========================
  // ✅ Barcode Scan (quick add)
  // =========================
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const barcodeAbortRef = useRef<AbortController | null>(null);
  const [barcodeTerm, setBarcodeTerm] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);

  // =========================
  // Helpers
  // =========================
  const lineHasDuplicateItemUom = (itemId: any, uom: any, excludeIndex: number) => {
    if (!itemId || !uom) return false;
    return (values.transactionsLinesList || []).some((l: any, i: number) => {
      if (i === excludeIndex) return false;
      return String(l?.itemId || '') === String(itemId) && String(l?.uom || '') === String(uom || '');
    });
  };

  const resetLine = (line: any) => ({
    ...line,
    itemId: null,
    description: '',
    uom: '',
    price: 0,
    qty: 1,
    total: 0,
    totalBeforeDiscount: 0,
    discountRate: 0,
    discountAmount: 0,
    vatRate: 0,
    vatAmount: 0,
    _discountMode: 'RATE'
  });

  const getInfo = (line: any) => {
    if (!line?.itemId || !line?.uom) return null;
    return detailsMap.get(`${line.itemId}|${line.uom}`) || null;
  };

  const getItemNameForLine = (line: any) => {
    const info = getInfo(line);
    const isEn = i18n.language === 'en';
    const name = isEn ? info?.name || info?.name2 : info?.name2 || info?.name;
    return name || line?.description || '';
  };

  const getUomNameForLine = (line: any) => {
    const info = getInfo(line);
    const isEn = i18n.language === 'en';
    const uomName = isEn
      ? info?.unitOfMeasureName || info?.unitOfMeasureName2
      : info?.unitOfMeasureName2 || info?.unitOfMeasureName;
    return uomName || '';
  };

  const getUomFactorForLine = (line: any) => {
    const info = getInfo(line);
    const isEn = i18n.language === 'en';
    const uomFactor = isEn ? info?.uomFactor || info?.uomFactor : info?.uomFactor || info?.uomFactor;
    return uomFactor || '';
  };

  // =========================
  // ✅ Recalc totals

  // const recalculateLineTotals = (line: any) => {
  //   const price = round2(line.price);
  //   const qty = round2(line.qty);
  //   const vatRate = clamp(round2(line.vatRate), 0, 100);

  //   // 1) إجمالي قبل الخصم
  //   const totalBeforeDiscount = round2(price * qty);

  //   // 2) تحديد الخصم المعتمد للحساب بدون لمس الحقل الآخر
  //   const rateVal = clamp(round2(line.discountRate), 0, 100);
  //   const amtValRaw = round2(line.discountAmount);

  //   // ✅ fallback ذكي: لو mode AMOUNT لكن amount = 0 و rate > 0 استخدم RATE (والعكس)
  //   const useAmount =
  //     (line._discountMode === 'AMOUNT' && amtValRaw > 0) ||
  //     (line._discountMode !== 'RATE' && amtValRaw > 0 && rateVal === 0);

  //   let discountApplied = 0;

  //   if (useAmount) {
  //     const amt = clamp(amtValRaw, 0, totalBeforeDiscount);
  //     line.discountAmount = round2(amt); // ✅ ثبّت قيمة الخصم فقط
  //     discountApplied = line.discountAmount; // ✅ المعتمد للحساب
  //     // ❌ لا تلمس discountRate
  //   } else {
  //     const rate = rateVal;
  //     line.discountRate = round2(rate); // ✅ ثبّت نسبة الخصم فقط
  //     discountApplied = round2((totalBeforeDiscount * rate) / 100);
  //     discountApplied = clamp(discountApplied, 0, totalBeforeDiscount);
  //     // ❌ لا تلمس discountAmount
  //   }

  //   // 3) الصافي قبل الضريبة
  //   const amountAfterDiscount = round2(totalBeforeDiscount - discountApplied);
  //   // debugger;
  //   // 4) الضريبة + الإجمالي شامل الضريبة
  //   // const vatAmount = round2((amountAfterDiscount * vatRate) / 100);
  //   var unitP = round2(amountAfterDiscount / qty);
  //   const Vat = trunc((unitP * vatRate) / 100, 2);
  //   const vatAmount = round2(Vat * qty);

  //   // const totalWithVat = round2(amountAfterDiscount + vatAmount);

  //   const totalWithVat = trunc(new Decimal(amountAfterDiscount).add(vatAmount), 2);

  //   // ✅ حفظ القيم
  //   line.vatRate = vatRate;
  //   line.totalBeforeDiscount = totalBeforeDiscount;

  //   // عندك حالياً total = قبل الضريبة
  //   line.total = amountAfterDiscount;
  //   line.vatAmount = vatAmount;

  //   // ✅ أضف هذا للحفظ (مفيد للعرض و/أو للتقارير)
  //   line.totalWithVat = totalWithVat;

  //   line._discountAppliedAmount = discountApplied;
  // };

  // الصحيحة أسفل
  const recalculateLineTotals = (line: any) => {
    // const price = round2(line.price);
    const price = line.price;
    const qty = round2(line.qty);
    const vatRate = clamp(round2(line.vatRate), 0, 100);

    // 1) إجمالي قبل الخصم
    const totalBeforeDiscount = round2(price * qty);

    // 2) تحديد الخصم المعتمد للحساب بدون لمس الحقل الآخر
    const rateVal = clamp(round2(line.discountRate), 0, 100);
    const amtValRaw = round2(line.discountAmount);

    // ✅ fallback ذكي: لو mode AMOUNT لكن amount = 0 و rate > 0 استخدم RATE (والعكس)
    const useAmount =
      (line._discountMode === 'AMOUNT' && amtValRaw > 0) ||
      (line._discountMode !== 'RATE' && amtValRaw > 0 && rateVal === 0);

    let discountApplied = 0;

    if (useAmount) {
      const amt = clamp(amtValRaw, 0, totalBeforeDiscount);
      line.discountAmount = round2(amt); // ✅ ثبّت قيمة الخصم فقط
      discountApplied = line.discountAmount; // ✅ المعتمد للحساب
      // ❌ لا تلمس discountRate
    } else {
      const rate = rateVal;
      line.discountRate = round2(rate); // ✅ ثبّت نسبة الخصم فقط
      discountApplied = round2((totalBeforeDiscount * rate) / 100);
      discountApplied = clamp(discountApplied, 0, totalBeforeDiscount);
      // ❌ لا تلمس discountAmount
    }

    // 3) الصافي قبل الضريبة
    const amountAfterDiscount = round2(totalBeforeDiscount - discountApplied);

    // 4) الضريبة + الإجمالي شامل الضريبة
    const vatAmount = round2((amountAfterDiscount * vatRate) / 100);
    const totalWithVat = round2(amountAfterDiscount + vatAmount);

    // ✅ حفظ القيم
    line.vatRate = vatRate;
    line.totalBeforeDiscount = totalBeforeDiscount;

    // عندك حالياً total = قبل الضريبة
    line.total = amountAfterDiscount;
    line.vatAmount = vatAmount;

    // ✅ أضف هذا للحفظ (مفيد للعرض و/أو للتقارير)
    line.totalWithVat = totalWithVat;

    line._discountAppliedAmount = discountApplied;
  };

  // =========================
  // ✅ linesKey: يتغير فقط عند (itemType,itemId,uom) لتفادي fetch عند تغيير qty/price
  // =========================

  const linesKey = useMemo(() => {
    const lines = values.transactionsLinesList || [];
    return lines.map((l: any) => `${l?.itemType || ''}:${l?.itemId || ''}:${l?.uom || ''}`).join('|');
  }, [values.transactionsLinesList]);

  // =========================
  // ✅ Update-mode: load missing details (names/barcodes/uoms)
  // =========================
  useEffect(() => {
    const run = async () => {
      if (!inventoryId) return;

      const lines = values.transactionsLinesList || [];

      const validLine = lines.filter((l: any) => l?.itemId !== null);
      // const valid = lines.filter((l: any) => l?.itemType && l?.itemId && l?.uom);
      // debugger

      // if (!valid.length) return;
      if (!validLine.length) return;

      const missingByType = new Map<string, Set<string>>();
      for (const l of validLine) {
        // for (const l of valid) {
        const key = `${l.itemId}|${l.uom}`;
        if (detailsMap.has(key)) continue;

        const type = String(l.itemType || DEFAULT_ITEM_TYPE);
        if (!missingByType.has(type)) missingByType.set(type, new Set());
        missingByType.get(type)!.add(String(l.itemId));
      }

      if (missingByType.size === 0) return;

      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setDetailsLoading(true);
      try {
        const merged = new Map(detailsMap);

        for (const [type, idsSet] of missingByType.entries()) {
          const itemIds = Array.from(idsSet);
          for (const idsChunk of chunk(itemIds, 200)) {
            const resp = await fetchItemsWithUomLookup(
              {
                InventoryId: inventoryId,
                Scope: 1,
                WithLastPrice: true
                // ✅ في التعديل عادة نسمح بإظهار كل شيء (لا نفلتر OnlyEnabled)
              },
              {
                pageIndex: 1,
                pageSize: 3000,
                itemType: type,
                itemIds: idsChunk
              },
              abortRef.current.signal
            );

            for (const r of resp.data?.data || []) {
              merged.set(`${r.itemId}|${r.unitOfMeasure}`, r);
            }
          }
        }

        setDetailsMap(merged);
      } catch (e: any) {
        if (e?.name !== 'CanceledError') console.error(e);
      } finally {
        setDetailsLoading(false);
      }
    };

    if (isUpdate === true) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId, linesKey, isUpdate]);

  // ✅ clear details when inventory changes
  useEffect(() => {
    setDetailsMap(new Map());
    loadedUomsRef.current.clear();
  }, [inventoryId]);

  // =========================
  // ✅ UOM options per item (for UOM column select)
  // =========================
  const uomsByItemId = useMemo(() => {
    const map = new Map<string, ItemWithUomRow[]>();

    for (const r of detailsMap.values()) {
      if (!r?.itemId) continue;
      const list = map.get(r.itemId) ?? [];
      list.push(r);
      map.set(r.itemId, list);
    }

    for (const [itemId, list] of map.entries()) {
      const uniq = new Map<string, ItemWithUomRow>();
      for (const r of list) uniq.set(r.unitOfMeasure, r);

      const arr = Array.from(uniq.values());
      arr.sort((a, b) => Number(b.isPrimaryUOM) - Number(a.isPrimaryUOM));
      map.set(itemId, arr);
    }

    return map;
  }, [detailsMap]);

  const loadedUomsRef = useRef(new Set<string>());
  const uomsAbortRef = useRef<AbortController | null>(null);
  const [uomsLoading, setUomsLoading] = useState(false);

  const ensureItemUomsLoaded = async (itemType: ItemType, itemId: string) => {
    if (!inventoryId || !itemId) return;

    const key = `${inventoryId}|${itemType}|${itemId}`;
    if (loadedUomsRef.current.has(key)) return;

    loadedUomsRef.current.add(key);

    try {
      uomsAbortRef.current?.abort();
      uomsAbortRef.current = new AbortController();

      setUomsLoading(true);

      const resp = await fetchItemsWithUomLookup(
        {
          InventoryId: inventoryId,
          Scope: 1,
          WithLastPrice: true
        },
        {
          pageIndex: 1,
          pageSize: 3000,
          itemType,
          itemIds: [itemId]
        },
        uomsAbortRef.current.signal
      );

      const list: ItemWithUomRow[] = resp?.data?.data || [];
      if (!Array.isArray(list) || list.length === 0) return;

      setDetailsMap(prev => {
        const merged = new Map(prev);
        for (const r of list) merged.set(`${r.itemId}|${r.unitOfMeasure}`, r);
        return merged;
      });
    } catch (e: any) {
      if (e?.name !== 'CanceledError') {
        loadedUomsRef.current.delete(key);
        console.error(e);
      }
    } finally {
      setUomsLoading(false);
    }
  };

  // ✅ keep barcode input focused when inventory is selected
  useEffect(() => {
    setBarcodeError(null);
    if (!inventoryId) return;
    const h = window.setTimeout(() => barcodeInputRef.current?.focus(), 50);
    return () => window.clearTimeout(h);
  }, [inventoryId]);

  // const pickBestRowForBarcode = (rows: ItemWithUomRow[]) => {
  //   if (!Array.isArray(rows) || rows.length === 0) return null;
  //   return rows.find(r => r?.isPrimaryUOM) || rows[0];
  // };

  const norm = (s: any) => String(s ?? '').trim();
  const pickBestRowForBarcode = (rows: ItemWithUomRow[], scannedBarcode: string) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const b = norm(scannedBarcode);

    // ✅ 1) اختر الصف الذي باركوده يساوي الباركود الممسوح (Exact match)
    const exact = rows.find(r => norm((r as any)?.barcode) === b);
    if (exact) return exact;

    // ✅ 2) أحياناً يرجع الحقل باسم مختلف (احتياط)
    const exact2 = rows.find(r => norm((r as any)?.barCode) === b || norm((r as any)?.barcodeValue) === b);
    if (exact2) return exact2;

    // ✅ 3) fallback
    return rows.find(r => (r as any)?.isPrimaryUOM) || rows[0];
  };

  const addLineByBarcode = async (rawBarcode: string) => {
    const barcode = String(rawBarcode || '').trim();
    if (!barcode) return;

    setBarcodeError(null);

    if (!inventoryId) {
      setBarcodeError(i18n.language === 'en' ? 'Select inventory first.' : 'اختر المخزن أولاً.');
      barcodeInputRef.current?.focus();
      return;
    }

    // ✅ Cancel previous scan request
    barcodeAbortRef.current?.abort();
    barcodeAbortRef.current = new AbortController();

    setBarcodeLoading(true);
    try {
      // ✅ For PO lines: barcode scan targets inventory items by default
      const itemType: ItemType = DEFAULT_ITEM_TYPE;

      const resp = await fetchItemsWithUomLookup(
        {
          InventoryId: inventoryId,
          Scope: 1,
          WithLastPrice: true
          // ...(isUpdate ? {} : { OnlyEnabled: true })
        },
        {
          pageIndex: 1,
          pageSize: 30,
          itemType,
          barcodeEqual: barcode
        } as any,
        barcodeAbortRef.current.signal
      );

      const list: ItemWithUomRow[] = resp?.data?.data || [];
      // const picked = pickBestRowForBarcode(list);
      const picked = pickBestRowForBarcode(list, barcode);

      if (!picked?.itemId || !picked?.unitOfMeasure) {
        setBarcodeError(i18n.language === 'en' ? 'Barcode not found.' : 'لا يوجد صنف بهذا الباركود.');
        barcodeInputRef.current?.focus();
        return;
      }

      // ✅ store for display
      setDetailsMap(prev => {
        const m = new Map(prev);
        m.set(`${picked.itemId}|${picked.unitOfMeasure}`, picked);
        return m;
      });

      // ✅ preload all UOMs for the item (so UOM select is ready)
      ensureItemUomsLoaded(itemType, picked.itemId);

      // ✅ add or increment qty (better UX for barcode scan)
      const lines = [...(values.transactionsLinesList || [])];
      const existingIndex = lines.findIndex(
        (l: any) =>
          String(l?.itemId || '') === String(picked.itemId) && String(l?.uom || '') === String(picked.unitOfMeasure)
      );

      if (existingIndex >= 0) {
        const current = { ...lines[existingIndex] };
        current.qty = Number(current.qty || 0) + 1;
        recalculateLineTotals(current);
        lines[existingIndex] = current;
        formik.setFieldValue('transactionsLinesList', lines);
      } else {
        const isEn = i18n.language === 'en';
        const desc = isEn ? picked.name || picked.name2 || '' : picked.name2 || picked.name || '';

        const itemVat =
          typeof picked?.itemVat_Rate === 'number' && Number.isFinite(picked.itemVat_Rate) ? picked.itemVat_Rate : 0;
        // debugger
        const newLine: any = {
          itemType,
          itemId: picked.itemId,
          lineNumber: lines.length + 1,
          uom: picked.unitOfMeasure,
          // price: Number(picked.unitPrice || 0),
          price: Number(picked.lastCostPrice || 0),
          qty: 1,
          discountRate: 0,
          discountAmount: 0,
          vatRate: Number(itemVat || 0),
          totalBeforeDiscount: 0,
          total: 0,
          vatAmount: 0,
          description: desc,
          id: null,
          inventoryId: inventoryId ?? '',
          _discountMode: 'RATE'
        };

        recalculateLineTotals(newLine);
        formik.setFieldValue('transactionsLinesList', [...lines, newLine]);
      }

      setBarcodeTerm('');
      barcodeInputRef.current?.focus();
    } catch (e: any) {
      if (e?.name !== 'CanceledError') {
        console.error(e);
        setBarcodeError(i18n.language === 'en' ? 'Failed to lookup barcode.' : 'تعذر البحث عن الباركود.');
      }
    } finally {
      setBarcodeLoading(false);
    }
  };

  // =========================
  // Line change
  // =========================
  const handleLineChange = (index: number, field: string, value: any) => {
    const updatedLines = [...(values.transactionsLinesList || [])];
    if (!updatedLines[index]) return;

    const currentLine = { ...updatedLines[index] };
    const currentType: ItemType = (currentLine.itemType || DEFAULT_ITEM_TYPE) as ItemType;

    // تغيير نوع الصنف
    if (field === 'itemType') {
      const nextType = (value || DEFAULT_ITEM_TYPE) as ItemType;

      if (nextType === 'UndefinedItem') {
        updatedLines[index] = {
          ...resetLine(currentLine),
          itemType: nextType,
          uom: EACH_UOM_ID, // ✅ Each ثابت
          qty: 1 // ✅ قيمة افتراضية
        };
      } else {
        updatedLines[index] = {
          ...resetLine(currentLine),
          itemType: nextType
        };
      }

      formik.setFieldValue('transactionsLinesList', updatedLines);
      return;
    }

    // // مسح الصنف
    // if (field === 'clearItem') {
    //   updatedLines[index] = {
    //     ...resetLine(currentLine),
    //     itemType: currentType
    //   };
    //   formik.setFieldValue('transactionsLinesList', updatedLines);
    //   return;
    // }
    // مسح الصنف
    if (field === 'clearItem') {
      const next = {
        ...resetLine(currentLine),
        itemType: currentType
      };
      if (currentType === 'UndefinedItem') next.uom = EACH_UOM_ID;

      updatedLines[index] = next;
      formik.setFieldValue('transactionsLinesList', updatedLines);
      return;
    }

    // تغيير الوحدة

    // تغيير الوحدة (لا ينطبق على UndefinedItem)
    if (field === 'uom') {
      if (currentType === 'UndefinedItem') return;

      const itemId = currentLine.itemId;
      if (itemId && lineHasDuplicateItemUom(itemId, value, index)) {
        window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
        return;
      }

      updatedLines[index] = { ...currentLine, uom: value };
      recalculateLineTotals(updatedLines[index]);
      formik.setFieldValue('transactionsLinesList', updatedLines);
      return;
    }
    // باقي الحقول
    updatedLines[index] = { ...currentLine, [field]: value };

    if (field === 'discountRate') {
      updatedLines[index]._discountMode = 'RATE';
      updatedLines[index].discountAmount = 0; // ✅ تصفير قيمة الخصم
    }
    if (field === 'discountAmount') {
      updatedLines[index]._discountMode = 'AMOUNT';
      updatedLines[index].discountRate = 0; // ✅ تصفير نسبة الخصم
    }

    if (['qty', 'price', 'vatRate', 'discountRate', 'discountAmount'].includes(field)) {
      recalculateLineTotals(updatedLines[index]);
    }

    formik.setFieldValue('transactionsLinesList', updatedLines);
  };

  // =========================
  // Add / Delete
  // =========================
  const handleAddLine = () => {
    const newLine = {
      itemType: DEFAULT_ITEM_TYPE,
      itemId: null,
      lineNumber: (values.transactionsLinesList?.length || 0) + 1,
      uom: '',
      price: 0,
      qty: 1,
      discountRate: 0,
      discountAmount: 0,
      vatRate: 0,
      totalBeforeDiscount: 0,
      total: 0,
      vatAmount: 0,
      description: '',
      id: null,
      inventoryId: inventoryId ?? '',
      _discountMode: 'RATE'
    };

    formik.setFieldValue('transactionsLinesList', [...(formik.values.transactionsLinesList || []), newLine]);
  };

  const handleDeleteLine = (index: number) => {
    const updatedLines = (values.transactionsLinesList || []).filter((_: any, i: number) => i !== index);
    formik.setFieldValue('transactionsLinesList', updatedLines);
  };

  // =========================
  // ✅ Open picker for line (select item + uom)
  // =========================
  const openPickerForLine = async (lineIndex: number) => {
    const lines = values.transactionsLinesList || [];
    const line = lines[lineIndex];
    if (!line) return;

    const isLegacyDescLine = !line?.itemId && (line?.itemType == null || line?.itemType === '');

    const itemType: ItemType = (line.itemType || DEFAULT_ITEM_TYPE) as ItemType;

    // ✅ امنع UndefinedItem الحقيقي فقط
    if (itemType === 'UndefinedItem' && !isLegacyDescLine) return;

    // ✅ في حالة itemType null نخلي الـ picker يشتغل كـ InventoryItem (أو DEFAULT)
    const itemTypeForPicker: ItemType = (line.itemType || DEFAULT_ITEM_TYPE) as ItemType;

    if (!inventoryId) {
      window?.alert?.(t('يجب اختيار المخزن أولاً.'));
      return;
    }

        const invId = itemTypeForPicker === 'InventoryItem' ? inventoryId : undefined;


    const picked = await pickItemUom({
      title: i18n.language === 'en' ? 'Pick Item & UOM' : 'اختيار الصنف',
      params: {
        // InventoryId: inventoryId,
        InventoryId: invId,
        Scope: 1,
        WithLastPrice: true
      },
      bodyDefaults: {
        itemType: itemTypeForPicker
      },
      pageSize: 10,
      // searchMode: 'auto',
      searchMode: 'manual',
      // initialSearchField: 'barcode',
      initialSearchField: 'barcodeEqual',
      isDisabledRow: (r: any) => lineHasDuplicateItemUom(r.itemId, r.unitOfMeasure, lineIndex),
      dynamicFields: ['lastCostPrice', 'uomFactor']
      //  fixedFields:['']
    });

    if (!picked) return;

    const row: any = picked.row;

    // ✅ منع التكرار
    if (lineHasDuplicateItemUom(row.itemId, row.unitOfMeasure, lineIndex)) {
      window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
      return;
    }

    // ✅ خزّن للعرض فوراً
    setDetailsMap(prev => {
      const m = new Map(prev);
      m.set(`${row.itemId}|${row.unitOfMeasure}`, row);
      return m;
    });

    // ✅ حدّث السطر
    const updatedLines = [...lines];
    const currentLine = { ...updatedLines[lineIndex] };

    const isEn = i18n.language === 'en';
    const desc = isEn ? row.name || row.name2 || '' : row.name2 || row.name || '';

    const itemVat =
      typeof row?.itemVat_Rate === 'number' && Number.isFinite(row.itemVat_Rate) ? row.itemVat_Rate : undefined;
    // debugger
    const suggestedPrice =
      (currentLine.price == null || Number(currentLine.price) === 0) && row?.unitPrice != null
        ? Number(row.unitPrice || 0)
        : Number(currentLine.price || 0);

    updatedLines[lineIndex] = {
      ...currentLine,
      itemType,
      itemId: row.itemId,
      uom: row.unitOfMeasure,
      description: desc,
      // price: suggestedPrice,
      price: row.lastCostPrice,
      qty: Number(currentLine.qty || 1),
      vatRate: Number(itemVat ?? currentLine.vatRate ?? 0)
    };

    recalculateLineTotals(updatedLines[lineIndex]);
    formik.setFieldValue('transactionsLinesList', updatedLines);

    // ✅ حمّل جميع وحدات الصنف ليظهر select في عمود الوحدة
    ensureItemUomsLoaded(itemType, row.itemId);
  };

  // =========================
  // VAT Rate Display (Single / Multiple)
  // =========================
  const vatRateDisplay = useMemo(() => {
    const lines = values.transactionsLinesList || [];
    const rates = Array.from(new Set(lines.map((l: any) => clamp(num(l?.vatRate), 0, 100).toFixed(2)))).filter(
      v => v !== '0.00'
    );

    if (rates.length === 0) return '0';
    if (rates.length === 1) return rates[0];
    return i18n.language === 'en' ? 'Multiple' : 'متعدد';
  }, [values.transactionsLinesList, i18n.language]);

  const calc = useDocumentCalculator(formik, {
    lines: 'transactionsLinesList',
    headerDiscountRate: 'discountRate',
    headerDiscountAmount: 'discountAmount',
    grossAmount: 'grossAmount',
    vat: 'vat',
    netAmount: 'netAmount'
  });

  // console.log(values.transactionsLinesList);

  return (
    <>
      <Loading loading={false} error={null} Type='Dots'>
        {/* ✅ one time modal */}
        <ItemUomPickerModal {...modalProps} />

        <div className=' border-0'>
          <Card>
            <div className=''>
              <div className='bg-inverse bg-opacity-10 p-3 rounded-0'>
                <div className='d-flex flex-wrap gap-4 justify-content-between'>
                  <div className='d-flex gap-2 align-items-center'>
                    <span className='fw-bold'>{t('الإجمالي')}:</span>
                    <span className='fw-bold'>{calc.totals.linesSubtotal}</span>
                  </div>
                  <div className='d-flex gap-2 align-items-center'>
                    <span className='fw-bold'>{t('VAT')}:</span>
                    <span className='fw-bold text-warning'>{calc.totals.vatTotal}</span>
                  </div>
                  <div className='d-flex gap-2 align-items-center'>
                    <span className='fw-bold'>{t('الصافي')}:</span>
                    <span className='text-theme fs-5 fw-bold'>{calc.totals.netTotal}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className='card-header'>
            <div className='d-flex flex-column flex-md-row justify-content-between gap-3'>
              {/* العنوان */}
              <div className='d-flex align-items-center'>
                <i className='fas fa-undo fs-5 text-theme me-2'></i>

                <h6 className='mb-0 fw-semibold'>
                  {t('بنود المردود')}
                  <span className='badge bg-theme rounded-pill ms-2'>{values.transactionsLinesList?.length || 0}</span>
                </h6>
              </div>

              {/* الأدوات */}
              <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
                {/* حالة التحميل */}
                {detailsLoading && isUpdate && (
                  <div className='d-flex align-items-center'>
                    <span className='spinner-border spinner-border-sm text-theme me-2'></span>
                    <small className='text-muted'>{t('جاري تحميل التفاصيل...')}</small>
                  </div>
                )}

                {/* الباركود */}
                <div className='position-relative w-100 w-md-auto'>
                  <div className='input-group input-group-sm w-100'>
                    <span className='input-group-text'>
                      <i className='fas fa-barcode text-muted'></i>
                    </span>

                    <input
                      ref={barcodeInputRef}
                      value={barcodeTerm}
                      onChange={e => {
                        setBarcodeTerm(e.target.value);
                        if (barcodeError) setBarcodeError(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addLineByBarcode(barcodeTerm);
                        }
                        if (e.key === 'Escape') {
                          setBarcodeTerm('');
                          setBarcodeError(null);
                        }
                      }}
                      className='form-control'
                      disabled={!inventoryId || barcodeLoading}
                      placeholder={t('امسح الباركود أو أدخل الرقم')}
                      inputMode='numeric'
                    />

                    {barcodeLoading ? (
                      <span className='input-group-text'>
                        <span className='spinner-border spinner-border-sm text-theme' />
                      </span>
                    ) : (
                      <button
                        className='input-group-text'
                        onClick={() => barcodeTerm && addLineByBarcode(barcodeTerm)}
                        disabled={!barcodeTerm || barcodeLoading || !inventoryId}
                        title={t('إضافة')}
                      >
                        <i className='fas fa-plus text-theme'></i>
                      </button>
                    )}
                  </div>

                  {/* رسالة الخطأ */}
                  {barcodeError && (
                    <div className='position-absolute top-100 start-0 mt-1 w-100' style={{ zIndex: 1025 }}>
                      <div className='alert alert-danger alert-sm py-1 mb-0 d-flex align-items-center'>
                        <i className='fas fa-exclamation-circle me-2'></i>
                        <small>{barcodeError}</small>
                      </div>
                    </div>
                  )}
                </div>

                {/* زر الإضافة */}
                <button
                  type='button'
                  className='btn btn-theme btn-sm d-flex align-items-center justify-content-center px-3 w-100 w-md-auto'
                  onClick={handleAddLine}
                >
                  <i className='fas fa-plus me-2'></i>
                  <span>{t('بند جديد')}</span>
                </button>
              </div>
            </div>
          </div>

          <div className='card-body p-0'>
            <div
              className='table-responsive'
              style={{ maxHeight: maxTableHeight, overflow: 'auto', position: 'relative' }}
            >
              <table className='table table-hover mb-0' style={{ minWidth: '1400px' }}>
                <thead
                  className=' shadow fw-bold'
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 101,
                    backdropFilter: 'blur(30px)'
                  }}
                >
                  <tr>
                    <th
                      className='text-center small shadow'
                      style={{
                        width: '50px',
                        minWidth: '50px',
                        position: 'sticky',
                        left: 0,
                        zIndex: 101
                      }}
                    >
                      #
                    </th>

                    <th className='text-center small' style={{ width: '120px', minWidth: '120px' }}>
                      {t('نوع الصنف')}
                    </th>

                    {/* ✅ merged cell: item + uom + barcode + actions */}
                    <th className='text-center small' style={{ width: '600px', minWidth: '600px' }}>
                      {t('الصنف / الباركود')}
                    </th>

                    <th className='text-center small' style={{ width: '120px', minWidth: '120px' }}>
                      {t('الوحدة')}
                    </th>

                    <th className='text-center small' style={{ width: '100px', minWidth: '80px' }}>
                      {t('الكمية')}
                    </th>

                    <th className='text-center small' style={{ width: '100px', minWidth: '80px' }}>
                      {t('السعر')}
                    </th>

                    <th className='text-center small' style={{ width: '100px', minWidth: '80px' }}>
                      {t('نسبة الخصم')}
                    </th>

                    <th className='text-center small' style={{ width: '100px', minWidth: '80px' }}>
                      {t('قيمة الخصم')}
                    </th>

                    <th className='text-center small' style={{ width: '100px', minWidth: '80px' }}>
                      VAT %
                    </th>

                    <th className='text-center small' style={{ width: '100px', minWidth: '80px' }}>
                      {t('VAT')}
                    </th>

                    <th className='text-center small' style={{ width: '100px', minWidth: '100px' }}>
                      {t('Total')}
                    </th>

                    <th className='text-center small' style={{ width: '50px', minWidth: '50px' }}></th>
                  </tr>
                </thead>

                <tbody>
                  {(values.transactionsLinesList || []).length > 0 ? (
                    (values.transactionsLinesList || []).map((line: any, index: number) => {
                      const itemType: ItemType = (line.itemType || DEFAULT_ITEM_TYPE) as ItemType;

                      const isDuplicate =
                        line.itemId &&
                        line.uom &&
                        (values.transactionsLinesList || []).some(
                          (l: any, i: number) =>
                            i !== index &&
                            String(l?.itemId || '') === String(line.itemId) &&
                            String(l?.uom || '') === String(line.uom || '')
                        );

                      const disablePick = !inventoryId;

                      const info = getInfo(line);
                      const itemName = line.itemId ? getItemNameForLine(line) : '';
                      const uomName = line.itemId ? getUomNameForLine(line) : '';
                      const uomFactor = line.itemId ? getUomFactorForLine(line) : '';

                      // console.log(line);

                      const barcode = info?.barcode || '';
                      const barcodeDisplay = line.itemId
                        ? barcode ||
                          (detailsLoading ? (i18n.language === 'en' ? 'Loading...' : '...جاري التحميل') : '-')
                        : '-';

                      const uomDisplay =
                        uomName ||
                        (detailsLoading
                          ? i18n.language === 'en'
                            ? 'Loading...'
                            : '...جاري التحميل'
                          : line.uom
                            ? // ? `ID: ${line.uom}`
                              `-`
                            : '-');

                      const uomOptions = line.itemId ? uomsByItemId.get(String(line.itemId)) || [] : [];

                      const isLegacyDescLine = !line?.itemId && (line?.itemType == null || line?.itemType === '');
                      const isUndefinedItem = String(line?.itemType || '') === 'UndefinedItem';

                      // ✅ عرض textarea في حالتين:
                      // 1) UndefinedItem الحقيقي
                      // 2) Legacy line القادم من التحويل (itemId null + itemType null)
                      const showDescTextarea = isUndefinedItem || isLegacyDescLine;

                      // ✅ السماح بفتح الـ picker فقط للـ Legacy line (حتى لو itemType null)
                      const canOpenPicker = !!inventoryId && (!isUndefinedItem || isLegacyDescLine);

                      return (
                        <tr key={index}>
                          <td
                            className='text-center align-middle bg-body shadow'
                            style={{ position: 'sticky', left: 0 }}
                          >
                            <span className='badge bg-theme bg-opacity-80 fw-bold' style={{ fontSize: '0.85rem' }}>
                              {index + 1}
                            </span>
                          </td>

                          {/* ✅ itemType */}
                          <td className='align-middle'>
                            <select
                              className='form-select form-select-sm border-theme bg-body'
                              value={itemType || ''}
                              onChange={e => handleLineChange(index, 'itemType', e.target.value)}
                              style={{ width: '100%' }}
                              disabled={isUpdate && !!line.id}
                            >
                              <option value='InventoryItem'>{i18n.language === 'en' ? 'Inventory' : 'مخزني'}</option>
                              <option value='ExpenseItem'>{i18n.language === 'en' ? 'Expense' : 'مصروف'}</option>
                              <option value='FixedAssetItem'>{i18n.language === 'en' ? 'Fixed Asset' : 'أصل'}</option>
                              <option value='ServiceItem'>{i18n.language === 'en' ? 'Service' : 'خدمي'}</option>
                            </select>
                          </td>

                          {/* ✅ Item + Barcode + Actions */}
                          <td className='align-middle'>
                            {showDescTextarea ? (
                              <div className='p-2 border border-theme bg-body'>
                                <Form.Control
                                  className='form-control form-control-sm border-theme'
                                  as='textarea'
                                  rows={1}
                                  value={line.description || ''}
                                  onChange={e => handleLineChange(index, 'description', e.target.value)}
                                  placeholder={i18n.language === 'en' ? 'Enter description...' : 'اكتب وصف الصنف...'}
                                />

                                <div className='d-flex gap-1 mt-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-theme rounded-0'
                                    onClick={() => openPickerForLine(index)}
                                    disabled={!canOpenPicker}
                                    title={!inventoryId ? t('يجب اختيار المخزن أولاً.') : t('اختيار صنف ووحدة')}
                                  >
                                    <i className='fas fa-search me-1'></i>
                                    {t('اختيار')}
                                  </button>

                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline-theme rounded-0'
                                    // onClick={openCreateNewItem}
                                    onClick={() => setShowFeatureModal(true)}
                                    title={t('إضافة صنف جديد')}
                                  >
                                    <i className='fas fa-plus me-1'></i>
                                    {t('صنف جديد')}
                                  </button>
                                </div>

                                <small className='text-muted d-block mt-2'>
                                  {t('باركود')}: <span className='fw-bold'>-</span>
                                </small>
                              </div>
                            ) : (
                              // ✅ خلي هنا نفس UI الحالي عندك للصنف العادي (كما هو)
                              <div style={{ width: '100%' }}>
                                <div className={`p-2 border ${isDuplicate ? 'border-danger' : 'border-theme'} bg-body`}>
                                  <div className='d-flex align-items-start gap-2'>
                                    <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                      {line.itemId ? (
                                        <>
                                          <span className='fw-bold text-truncate d-block'>
                                            {itemName ||
                                              (detailsLoading
                                                ? i18n.language === 'en'
                                                  ? 'Loading...'
                                                  : '...جاري التحميل'
                                                : `ID: ${line.itemId}`)}
                                          </span>

                                          <small className='text-muted d-block'>
                                            {t('الوحدة')}: <span className='fw-bold text-theme'>{uomDisplay}</span>
                                            <span className='mx-2'>•</span>
                                            {t('التعادل')}: <span className='fw-bold text-theme'>{uomFactor}</span>
                                            <span className='mx-2'>•</span>
                                            {t('باركود')}: <span className='fw-bold'>{barcodeDisplay}</span>
                                          </small>
                                        </>
                                      ) : (
                                        <span className='text-muted'>{t('اختر الصنف')}</span>
                                      )}

                                      {isDuplicate && (
                                        <small className='text-danger d-block mt-1'>
                                          <i className='fas fa-exclamation-triangle me-1'></i>
                                          {t('هذا الصنف مكرر مع نفس الوحدة')}
                                        </small>
                                      )}
                                    </div>

                                    <div className='d-flex gap-1'>
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-theme rounded-0'
                                        onClick={() => openPickerForLine(index)}
                                        disabled={disablePick}
                                        title={!inventoryId ? t('يجب اختيار المخزن أولاً.') : t('اختيار صنف ووحدة')}
                                      >
                                        <i className='fas fa-search me-1'></i>
                                        {line.itemId ? t('تغيير') : t('اختيار')}
                                      </button>

                                      {line.itemId && (
                                        <button
                                          type='button'
                                          className='btn btn-sm btn-outline-danger rounded-0'
                                          onClick={() => handleLineChange(index, 'clearItem', true)}
                                          title={t('مسح الصنف')}
                                          disabled={isUpdate && !!line.id}
                                        >
                                          <i className='fas fa-times'></i>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>

                          {/* ✅ UOM select */}
                          <td className='align-middle'>
                            <select
                              disabled={!line.itemId}
                              className={`form-select  form-select-sm bg-body ${
                                !line.itemId ? 'bg-secondary bg-opacity-40' : ''
                              } ${isDuplicate ? 'border-danger' : 'border-theme'}`}
                              value={line.uom || ''}
                              onFocus={() => {
                                if (line.itemId) ensureItemUomsLoaded(itemType, String(line.itemId));
                              }}
                              onChange={e => handleLineChange(index, 'uom', e.target.value)}
                              style={{ width: '100%' }}
                            >
                              <option value=''>{i18n.language === 'ar' ? 'اختر الوحدة' : 'Select UOM'}</option>

                              {/* لو ما حملنا خيارات بعد: اعرض الحالية */}
                              {line.uom && uomOptions.length === 0 && <option value={line.uom}>{uomDisplay}</option>}

                              {uomOptions.map(u => {
                                const isEn = i18n.language === 'en';
                                const label =
                                  (isEn
                                    ? u.unitOfMeasureName || u.unitOfMeasureName2
                                    : u.unitOfMeasureName2 || u.unitOfMeasureName) || `ID: ${u.unitOfMeasure}`;

                                return (
                                  <option key={u.unitOfMeasure} value={u.unitOfMeasure}>
                                    {label}
                                  </option>
                                );
                              })}

                              {uomsLoading && line.itemId ? (
                                <option value={line.uom || ''} disabled>
                                  {i18n.language === 'en' ? 'Loading...' : '...جاري التحميل'}
                                </option>
                              ) : null}
                            </select>
                          </td>

                          {/* ✅ qty */}
                          <td className='align-middle'>
                            <NumericFormat
                              name={`line-${index}-qty`}
                              value={line.qty}
                              thousandSeparator=','
                              decimalSeparator='.'
                              decimalScale={2}
                              fixedDecimalScale={false}
                              allowNegative={false}
                              className='form-control form-control-sm border-theme bg-body'
                              onValueChange={v => handleLineChange(index, 'qty', v.floatValue ?? 0)}
                              disabled={!line.itemId}
                              style={{ width: '100%' }}
                            />
                          </td>

                          {/* ✅ price */}
                          <td className='align-middle'>
                            <NumericFormat
                              name={`line-${index}-price`}
                              value={line.price}
                              thousandSeparator=','
                              decimalSeparator='.'
                              decimalScale={6}
                              fixedDecimalScale={false}
                              allowNegative={false}
                              className='form-control form-control-sm border-theme bg-body'
                              onValueChange={v => handleLineChange(index, 'price', v.floatValue ?? 0)}
                              disabled={!line.itemId}
                              style={{ width: '100%' }}
                            />
                          </td>

                          {/* ✅ discount rate */}
                          <td className='align-middle'>
                            <NumericFormat
                              name={`line-${index}-discountRate`}
                              value={line.discountRate}
                              thousandSeparator=','
                              decimalSeparator='.'
                              decimalScale={2}
                              fixedDecimalScale={false}
                              allowNegative={false}
                              isAllowed={v => v.floatValue === undefined || v.floatValue <= 100}
                              className='form-control form-control-sm border-theme bg-body'
                              onValueChange={v => handleLineChange(index, 'discountRate', v.floatValue ?? 0)}
                              disabled={!line.itemId}
                              style={{ width: '100%' }}
                            />
                          </td>

                          {/* ✅ discount amount */}
                          <td className='align-middle'>
                            <NumericFormat
                              name={`line-${index}-discountAmount`}
                              value={line.discountAmount}
                              thousandSeparator=','
                              decimalSeparator='.'
                              decimalScale={2}
                              fixedDecimalScale={false}
                              allowNegative={false}
                              className='form-control form-control-sm border-theme bg-body'
                              onValueChange={v => handleLineChange(index, 'discountAmount', v.floatValue ?? 0)}
                              disabled={!line.itemId}
                              style={{ width: '100%' }}
                            />
                          </td>

                          {/* ✅ vat rate */}
                          <td className='align-middle'>
                            <NumericFormat
                              name={`line-${index}-vatRate`}
                              value={line.vatRate}
                              thousandSeparator=','
                              decimalSeparator='.'
                              decimalScale={2}
                              fixedDecimalScale={false}
                              allowNegative={false}
                              isAllowed={v => v.floatValue === undefined || v.floatValue <= 100}
                              className='form-control form-control-sm border-theme bg-body'
                              onValueChange={v => handleLineChange(index, 'vatRate', v.floatValue ?? 0)}
                              disabled={!line.itemId}
                              style={{ width: '100%' }}
                            />
                          </td>

                          {/* ✅ vat amount */}
                          <td className='align-middle fw-bold text-warning text-center'>
                            <div style={{ width: '100%' }}>{formatNumber(line.vatAmount || 0)}</div>
                          </td>

                          {/* ✅ total */}
                          <td className='align-middle'>
                            <div style={{ width: '100%' }}>
                              {/* <strong className='text-theme fs-6'>{formatNumber(line.total || 0)}</strong> */}
                              <strong className='text-theme fs-6'>
                                {formatNumber(
                                  line.totalWithVat != null
                                    ? line.totalWithVat
                                    : Number(line.total || 0) + Number(line.vatAmount || 0)
                                )}
                              </strong>
                            </div>
                          </td>

                          {/* ✅ delete */}
                          <td className='align-middle text-center'>
                            <button
                              type='button'
                              className='btn btn-sm btn-danger rounded-0'
                              onClick={() => handleDeleteLine(index)}
                              title={t('Delete Line')}
                              style={{ width: '28px', height: '28px', padding: '0' }}
                            >
                              <i className='fas fa-trash text-white'></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={12} className='text-center py-4 text-muted'>
                        <i className='fa fa-inbox fa-2x mb-2'></i>
                        <br />
                        {t('لا توجد بنود مضافة لهذا الطلب')}
                        <br />
                        <button type='button' className='btn btn-theme mt-3' onClick={handleAddLine}>
                          <i className='fas fa-plus me-2'></i>
                          {t('إضافة أول بند')}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {Array.isArray(formik.errors.transactionsLinesList) && (
              <div className='bg-danger-subtle p-2 text-danger text-center small fw-bold'>
                {' '} 
                {t('يرجى التحقق من صحة بيانات البنود ')}{' '}
              </div>
            )}

            {/* {formik.errors.transactionsLinesList?.length && <div className='text-danger'> * مطلوب </div>} */}

            {(values.transactionsLinesList || []).length > 5 && (
              <div className='mt-2 text-center'>
                <small className='text-muted'>
                  <i className='fas fa-arrows-alt-v me-1'></i>
                  {t('اسحب لرؤية المزيد من البنود')}
                </small>
              </div>
            )}
          </div>

          {/* ========================= */}
          {/* ✅ خصم الفاتورة (Header) */}
          {/* ========================= */}
          <div className='card-body p-3 '>
            {/* <DocTotalsPanel
              discountAmountValue={Number(values.discountAmount || 0)}
              discountRateValue={Number(values.discountRate || 0)}
              netTotal={calc.totals.netTotal}
              onDiscountAmountChange={calc.onHeaderDiscountAmountChange}
              onDiscountRateChange={calc.onHeaderDiscountRateChange}
              subtotal={calc.totals.linesSubtotal}
              vatRateDisplay={vatRateDisplay as string}
              vatTotal={calc.totals.vatTotal}
              t={t}
            /> */}
          </div>

          <div className='card-footer d-flex justify-content-between align-items-center'>
            <div className='text-muted small'>
              <i className='fas fa-list me-1'></i>
              عرض {(values.transactionsLinesList || []).length} من {(values.transactionsLinesList || []).length} بند
            </div>
            <div className='text-theme fw-bold'>
              <i className='fas fa-calculator me-2'></i>
              {/* {t('إجمالي الطلب')}: {totals.total} */}
            </div>
          </div>
        </div>
      </Loading>

      {/* ✅ Feature Under Development Modal */}
      <NeatixModal
        show={showFeatureModal}
        setShow={setShowFeatureModal}
        centered
        size={'md'}
        showHeader={false}
        bodyClassName='p-0'
      >
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>{t('تنبيه')}</h5>
            <button type='button' className='btn-close' onClick={() => setShowFeatureModal(false)} />
          </div>
          <div className='modal-body'>
            <div className='d-flex align-items-center gap-2'>
              <i className='fas fa-tools text-warning fs-5'></i>
              <span className='fw-semibold'>{t('الميزة قيد التطوير')}</span>
            </div>
            <small className='text-muted d-block mt-2'>
              {i18n.language === 'en'
                ? 'This feature is under development and will be available soon.'
                : 'هذه الميزة قيد التطوير وستكون متاحة قريباً.'}
            </small>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-theme' onClick={() => setShowFeatureModal(false)}>
              {t('حسناً')}
            </button>
          </div>
        </div>
      </NeatixModal>
    </>
  );
};

export default POLines;
